"use client";

import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { Streamdown } from 'streamdown';

import "./ai-assistant.styles.scss";

export default function AIAssistantPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Thinking...");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  async function handleSubmit(e) {
    e.preventDefault();
    const question = input.trim();
    if (!question || isLoading) return;

    const userMsg = {
      id: uuidv4(),
      role: "user",
      content: question,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);
    setLoadingMessage("Thinking...");

    const timer = setTimeout(() => {
      setLoadingMessage("Thinking longer for a better answer...");
    }, 3000); // 3 seconds

    try {
      const answer = await askQuestion(question);
      const assistantMsg = {
        id: uuidv4(),
        role: "assistant",
        content: answer,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const errMsg = {
        id: uuidv4(),
        role: "assistant",
        content: "Sorry, something went wrong. Please try again or contact our support team if the problem persists.",
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      clearTimeout(timer);
      setIsLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // stop newline
      if (!isLoading && input.trim()) {
        // call your submit handler
        handleSubmit(e);
      }
    }
  }

  return (
    <main className="flex min-h-screen flex-col bg-[#f3f4f6] text-gray-900">
      {/* HEADER */}
      <header className="border-b fixed top-0 z-50 w-full border-gray-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <div>
            <h1 className="text-base font-semibold text-gray-800">
              Aspire Executive AI Assistant (PoC)
            </h1>
            <p className="text-xs text-gray-500">This AI has been built to answer questions on ICAG - Corporate Reporting Manual 2019-2024.</p>
          </div>

          <span className="rounded-full bg-gray-100 px-3 py-1 text-[10px] text-gray-500 border border-gray-200">
            PoC
          </span>
        </div>
      </header>

      {/* CHAT AREA */}
      <div className="flex flex-1 flex-col mt-18">
        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 sm:px-6">
          <div className="flex-1 overflow-y-auto pb-32 pt-6 flex">
  {messages.length === 0 && !isLoading ? (
    // Empty state: perfectly centered
    <div className="m-auto flex flex-col items-center text-center text-gray-500">
      <h2 className="mb-2 text-4xl font-semibold text-gray-700">
        Welcome
      </h2>
      <p className="max-w-md text-lg text-gray-500">
        Ask a question on ICAG - Corporate Reporting Manual 2019-2024.
      </p>
    </div>
  ) : (
    // Normal chat state
    <div className="flex w-full flex-col space-y-6">
      {messages.map((msg) => (
        <ChatBubble key={msg.id} role={msg.role} content={msg.content} />
      ))}

      {isLoading && (
        <div className="flex items-start gap-3">
          <Avatar label="AI" variant="assistant" />
          <div className="inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm text-gray-600 border border-gray-200">
            <span className="inline-flex h-1.5 w-1.5 animate-bounce rounded-full bg-gray-500" />
            <span className="inline-flex h-1.5 w-1.5 animate-bounce rounded-full bg-gray-500 [animation-delay:120ms]" />
            <span className="inline-flex h-1.5 w-1.5 animate-bounce rounded-full bg-gray-500 [animation-delay:240ms]" />
            <span className="ml-1">{loadingMessage}</span>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  )}
</div>

          {/* INPUT BAR */}
          <div className="pointer-events-none fixed bottom-0 left-0 right-0 bg-[#f3f4f6] pb-4">
            <div className="pointer-events-auto mx-auto w-full max-w-3xl px-4 sm:px-6">
              <form onSubmit={handleSubmit} className="flex items-center gap-3">
                {/* Clean, single-border wrapper */}
                <div className="flex-1 rounded-2xl bg-white border border-gray-300 shadow-sm focus-within:border-slate-500 focus-within:shadow-none">
                  <textarea
                    rows={1}
                    placeholder="Send a message..."
                    className="max-h-40 w-full resize-none rounded-2xl border-none bg-transparent px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-white text-sm transition
                    ${
                      isLoading || !input.trim()
                        ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                        : "bg-slate-800 hover:bg-slate-700"
                    }`}
                >
                  ➤
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

/* ------------------------------ */

function ChatBubble({ role, content }) {
  const isUser = role === "user";

  return (
    <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && <Avatar label="AI" variant="assistant" />}

      <div
        className={`
          max-w-[75%] px-4 py-2 text-[15px] leading-relaxed rounded-3xl
          ${
            isUser
              ? // USER BUBBLE — clean modern slate
                "bg-slate-200 text-slate-800 border border-slate-300"
              : // AI BUBBLE — soft modern glassy effect
                "bg-white/70 border border-gray-200"
          }
        `}
      >
        {/* Markdown rendering wrapper */}
        <div className="prose prose-sm max-w-none prose-p:m-0 prose-ul:pl-5 prose-ol:pl-5 prose-li:my-1 prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded">
          <Streamdown>
            {content}
          </Streamdown>
        </div>
      </div>

      {isUser && <Avatar label="You" variant="user" />}
    </div>
  );
}

/* ------------------------------ */

function Avatar({
  label,
  variant,
}) {
  const isUser = variant === "user";

  return (
    <div
      className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-medium
        ${
          isUser
            ? "ml-2 bg-slate-300 text-slate-700"
            : "mr-2 bg-red-50 text-red-600"
        }
      `}
    >
      {label}
    </div>
  );
}

/* ------------------------------ */

const AI_SERVICE_BASE_URL =
  process.env.AI_SERVICE_BASE_URL ?? "https://e185da1ab53a.ngrok-free.app/chat";

async function askQuestion(question) {
  const response = await fetch(AI_SERVICE_BASE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query: question }),
  });

  if (!response.ok) {
    let detail = "";
    try {
      const errorBody = await response.text();
      detail = errorBody ? ` (${errorBody})` : "";
    } catch {
      // ignore
    }
    throw new Error(
      `Request failed with status ${response.status}${detail}`,
    );
  }

  const data = await response.json();

  return data.ai_response;
}